/**
 * Functionality Access Context Provider
 * 
 * Provides app-wide access to functionality/feature access state.
 * Use this provider at the app root level to enable feature gating
 * across all components.
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  getFunctionalityAccess,
  clearFunctionalityAccessCache,
  FEATURE_CODES,
  isFeatureActive,
  hasUnlimitedCoaching as checkUnlimitedCoaching,
  getCoachingMinutesFromAccess,
} from '../Services/functionalityAccess';
import cookies from 'js-cookie';

// Create context
const FunctionalityAccessContext = createContext(null);

// Custom hook to use the context
export const useFunctionalityAccess = () => {
  const context = useContext(FunctionalityAccessContext);
  if (!context) {
    throw new Error('useFunctionalityAccess must be used within a FunctionalityAccessProvider');
  }
  return context;
};

export const FunctionalityAccessProvider = ({ children }) => {
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const fetchingRef = useRef(false);

  /**
   * Fetch functionality access from API
   */
  const fetchAccess = useCallback(async (forceRefresh = false) => {
    console.log('[FunctionalityAccess] fetchAccess called, forceRefresh:', forceRefresh);
    
    // Only fetch if user is authenticated
    if (!cookies.get('idtoken')) {
      console.log('[FunctionalityAccess] No idtoken found, skipping fetch');
      setAccess(null);
      setLoading(false);
      setInitialized(true);
      return;
    }

    // Prevent duplicate fetches
    if (fetchingRef.current && !forceRefresh) {
      console.log('[FunctionalityAccess] Already fetching, skipping');
      return;
    }
    
    fetchingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      console.log('[FunctionalityAccess] Calling getFunctionalityAccess API...');
      const data = await getFunctionalityAccess(forceRefresh);
      console.log('[FunctionalityAccess] API response:', data);
      setAccess(data);
      setInitialized(true);
    } catch (err) {
      setError(err.message || 'Failed to fetch functionality access');
      console.error('[FunctionalityAccess] Error:', err);
      // Set initialized even on error to prevent infinite loading
      setInitialized(true);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, []);

  /**
   * Refresh access data (force fetch from API)
   */
  const refreshAccess = useCallback(() => {
    clearFunctionalityAccessCache();
    return fetchAccess(true);
  }, [fetchAccess]);

  /**
   * Check if user has access to a specific feature
   */
  const hasFeature = useCallback((featureCode) => {
    if (!access?.functionalities) return false;
    return isFeatureActive(access.functionalities, featureCode);
  }, [access]);

  /**
   * Check if user has coaching access
   */
  const hasCoachingAccess = useCallback(() => {
    return hasFeature(FEATURE_CODES.COACHING);
  }, [hasFeature]);

  /**
   * Check if user has faith access
   */
  const hasFaithAccess = useCallback(() => {
    return hasFeature(FEATURE_CODES.FAITH);
  }, [hasFeature]);

  /**
   * Check if user has unlimited coaching
   */
  const hasUnlimitedCoaching = useCallback(() => {
    return checkUnlimitedCoaching(access);
  }, [access]);

  /**
   * Get coaching minutes info
   */
  const getCoachingMinutes = useCallback(() => {
    if (!access?.functionalities) {
      return { available: 0, unlimited: false };
    }
    return getCoachingMinutesFromAccess(access.functionalities);
  }, [access]);

  /**
   * Get feature status for a specific feature
   */
  const getFeatureStatus = useCallback((featureCode) => {
    const functionality = access?.functionalities?.[featureCode];
    if (!functionality) {
      return {
        hasAccess: false,
        status: 'inactive',
        endDate: null,
        metaInfo: null,
      };
    }
    return {
      hasAccess: functionality.status === 'active',
      status: functionality.status,
      endDate: functionality.end_date,
      metaInfo: functionality.meta_info,
    };
  }, [access]);

  /**
   * Clear access data (for logout)
   */
  const clearAccess = useCallback(() => {
    clearFunctionalityAccessCache();
    setAccess(null);
    setInitialized(false);
    setError(null);
  }, []);

  // Auto-fetch on mount if authenticated
  useEffect(() => {
    console.log('[FunctionalityAccess] Mount effect running, idtoken:', !!cookies.get('idtoken'));
    if (cookies.get('idtoken')) {
      fetchAccess();
    } else {
      console.log('[FunctionalityAccess] No token on mount, setting initialized');
      setLoading(false);
      setInitialized(true);
    }
  }, [fetchAccess]);

  // Poll for token changes (handles login scenario where token is set after mount)
  useEffect(() => {
    const checkForToken = () => {
      const hasToken = !!cookies.get('idtoken');
      // Fetch if we have a token but no access data yet
      if (hasToken && !access && !fetchingRef.current) {
        console.log('[FunctionalityAccess] Token detected, no access data, fetching...');
        fetchAccess();
      }
    };

    // Check periodically for token (handles login redirect)
    const intervalId = setInterval(checkForToken, 1000);
    
    // Also check on visibility change (tab focus)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkForToken();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [access, fetchAccess]);

  // Listen for profile/auth changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'profile') {
        const hasToken = cookies.get('idtoken');
        if (hasToken) {
          // Profile changed while logged in, refresh access
          refreshAccess();
        } else {
          // Logged out
          clearAccess();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [refreshAccess, clearAccess]);

  const value = {
    // State
    access,
    loading,
    error,
    initialized,
    
    // Feature checks
    hasFeature,
    hasCoachingAccess,
    hasFaithAccess,
    hasUnlimitedCoaching,
    getCoachingMinutes,
    getFeatureStatus,
    
    // Actions
    refreshAccess,
    fetchAccess,
    clearAccess,
    
    // Constants
    FEATURE_CODES,
  };

  return (
    <FunctionalityAccessContext.Provider value={value}>
      {children}
    </FunctionalityAccessContext.Provider>
  );
};

export default FunctionalityAccessProvider;
